import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Loader2, Eye, EyeOff, Signal } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const EMPTY = { name: "", operator: "", price: "", data: "", calls: "Appels, SMS & MMS illimités",
  features: [], commitment: "Sans engagement", published: true, order: 0 };

export default function AdminPlans() {
  const [plans, setPlans] = useState(null);
  const [form, setForm] = useState(null);
  const [featuresText, setFeaturesText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
  useEffect(() => { load(); }, []);

  const openEditor = (p) => {
    setForm(p ? { ...p, price: String(p.price) } : { ...EMPTY, order: (plans?.length || 0) + 1 });
    setFeaturesText((p?.features || []).join(", "));
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Le nom du forfait est requis"); return; }
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price) || 0,
      features: featuresText.split(",").map((s) => s.trim()).filter(Boolean) };
    delete payload.id; delete payload.demo;
    try {
      if (form.id) await api.put(`/admin/plans/${form.id}`, payload);
      else await api.post("/admin/plans", payload);
      toast.success("Forfait enregistré");
      setForm(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le forfait « ${p.name} » ?`)) return;
    await api.delete(`/admin/plans/${p.id}`);
    toast.success("Forfait supprimé");
    load();
  };

  const toggle = async (p) => {
    const payload = { ...p, published: !p.published };
    delete payload.id; delete payload.demo;
    await api.put(`/admin/plans/${p.id}`, payload);
    toast.success(p.published ? "Forfait masqué" : "Forfait publié");
    load();
  };

  if (form) {
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    return (
      <div className="mx-auto max-w-xl" data-testid="plan-editor">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-2xl font-extrabold text-slate-900">{form.id ? "Modifier le forfait" : "Nouveau forfait"}</h1>
          <button data-testid="plan-editor-close" onClick={() => setForm(null)} className="rounded-full bg-slate-100 p-2.5 text-slate-500"><X className="h-5 w-5" /></button>
        </div>
        <div className="card-oxy mt-5 space-y-4 p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label-oxy">Nom du forfait</label>
              <input data-testid="plan-field-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="input-oxy" placeholder="Forfait 100 Go" />
            </div>
            <div>
              <label className="label-oxy">Opérateur</label>
              <input data-testid="plan-field-operator" value={form.operator} onChange={(e) => set("operator", e.target.value)} className="input-oxy" placeholder="Orange, SFR…" />
            </div>
            <div>
              <label className="label-oxy">Prix (€/mois)</label>
              <input data-testid="plan-field-price" type="number" min="0" step="0.01" inputMode="decimal" value={form.price} onChange={(e) => set("price", e.target.value)} className="input-oxy" />
            </div>
            <div>
              <label className="label-oxy">Internet mobile</label>
              <input data-testid="plan-field-data" value={form.data} onChange={(e) => set("data", e.target.value)} className="input-oxy" placeholder="100 Go" />
            </div>
          </div>
          <div>
            <label className="label-oxy">Appels / SMS</label>
            <input data-testid="plan-field-calls" value={form.calls} onChange={(e) => set("calls", e.target.value)} className="input-oxy" />
          </div>
          <div>
            <label className="label-oxy">Points forts (séparés par des virgules)</label>
            <input data-testid="plan-field-features" value={featuresText} onChange={(e) => setFeaturesText(e.target.value)}
              className="input-oxy" placeholder="5G, Roaming Europe" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label-oxy">Engagement</label>
              <select data-testid="plan-field-commitment" value={form.commitment} onChange={(e) => set("commitment", e.target.value)} className="input-oxy">
                <option>Sans engagement</option>
                <option>Engagement 12 mois</option>
                <option>Engagement 24 mois</option>
              </select>
            </div>
            <div>
              <label className="label-oxy">Ordre d'affichage</label>
              <input data-testid="plan-field-order" type="number" inputMode="numeric" value={form.order} onChange={(e) => set("order", parseInt(e.target.value, 10) || 0)} className="input-oxy" />
            </div>
          </div>
          <label className="flex items-center gap-3 rounded-xl bg-slate-50 p-3.5">
            <input type="checkbox" data-testid="plan-field-published" checked={form.published} onChange={(e) => set("published", e.target.checked)}
              className="h-5 w-5 accent-[#D4AF37]" />
            <span className="text-sm font-semibold text-slate-700">Publié sur le site</span>
          </label>
          <button data-testid="plan-save-btn" onClick={save} disabled={saving} className="btn-gold w-full disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Enregistrer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-plans-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-slate-900">Forfaits mobiles</h1>
          <p className="mt-1 text-xs text-slate-500">Les demandes de souscription des clients apparaissent dans Demandes → Forfaits.</p>
        </div>
        <button data-testid="plan-create-btn" onClick={() => openEditor(null)} className="btn-gold !px-5 !py-2.5 text-sm">
          <Plus className="h-4 w-4" /> Nouveau forfait
        </button>
      </div>

      {plans === null ? (
        <div className="mt-5 space-y-3">{Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-slate-100" />)}</div>
      ) : plans.length === 0 ? (
        <div className="mt-8 rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="plans-empty">
          <Signal className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-2 font-display font-semibold text-slate-800">Aucun forfait</p>
        </div>
      ) : (
        <ul className="mt-5 space-y-3" data-testid="plans-list">
          {plans.map((p) => (
            <li key={p.id} data-testid={`plan-row-${p.id}`} className="card-oxy flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="font-display font-bold text-slate-900">
                  {p.name}
                  {p.demo && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-700">Exemple</span>}
                  {!p.published && <span className="ml-2 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">Masqué</span>}
                </p>
                <p className="mt-0.5 text-xs text-slate-500">{p.operator || "—"} · {p.data} · {p.commitment}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <span className="rounded-full bg-gold-soft px-3 py-1.5 text-sm font-bold text-gold-dark">{p.price} €/mois</span>
                <button data-testid={`plan-toggle-${p.id}`} onClick={() => toggle(p)} className="rounded-xl bg-slate-100 p-2.5 text-slate-500 hover:bg-gold-soft hover:text-gold-dark">
                  {p.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <button data-testid={`plan-edit-${p.id}`} onClick={() => openEditor(p)} className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600 hover:bg-gold-soft">Modifier</button>
                <button data-testid={`plan-delete-${p.id}`} onClick={() => remove(p)} className="rounded-xl bg-slate-100 p-2.5 text-slate-500 hover:bg-red-100 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
